import type { DocPage } from '../types';

/**
 * Surcharges EN des pages doc prioritaires (POS, clients, stock).
 * Les clés absentes restent en FR via le merge dans resolveDocPages.
 */
export const docPagesEn: Partial<Record<string, DocPage>> = {
  '/pos/transaction': {
    key: '/pos/transaction',
    title: 'New sale',
    path: '/pos/transaction',
    definition:
      'Main checkout (POS) screen. Record a sale by combining services and/or products, a client, any discounts, then complete payment with one or more payment methods.',
    utility:
      'This is the commercial entry point: every validated sale feeds history, cash reports, client loyalty, stock (sold or consumed products), and can trigger manufacturing orders or installment payments depending on configuration.',
    relatedModules: [
      { label: 'Services', path: '/services', relation: 'Catalog of services sold at checkout (price, duration, manufacturing model).' },
      { label: 'Products', path: '/inventory/products', relation: 'Items sold or scanned; stock decremented on validation.' },
      { label: 'Clients', path: '/clients', relation: 'Client profile, loyalty wallet, history; required for some flows (manufacturing, installment payments).' },
      { label: 'Installment payments', path: '/payment-plans', relation: 'Pay in installments from the cart (0 deposit allowed) instead of full payment.' },
      { label: 'Manufacturing orders', path: '/manufacturing/orders', relation: 'Created automatically if a cart service is configured in manufacturing mode.' },
      { label: 'Reports', path: '/pos/reports', relation: 'Summary of payments from validated transactions.' },
    ],
    whenToUse: [
      'For every in-store or counter sale.',
      'For a single or split payment (multiple payment methods).',
      'When the sale must be linked to an identified client (loyalty, manufacturing, follow-up).',
    ],
    keyActions: [
      'Build the cart (services, products, quantities).',
      'Search or create a client; apply promotions and loyalty wallet.',
      'Split payment (cash, mobile money, transfer, etc.).',
      'Print or download the receipt (PDF).',
    ],
    workflow: [
      'Open checkout and add lines to the cart.',
      'Check totals, discounts and linked client.',
      'Choose settlement (full payment or installments).',
      'Validate: the sale is saved and related modules are updated.',
    ],
    importantNotes: [
      'Payment total must exactly match cart total; otherwise validation is rejected.',
      'One transaction can group multiple lines (services + products) under the same receipt number.',
      '“Manufacturing” services require an identified client before validation.',
    ],
    checks: ['Consistent amounts', 'Client filled when required', 'Enough stock for products sold'],
  },

  '/pos/prestations-history': {
    key: '/pos/prestations-history',
    title: 'Services history',
    path: '/pos/prestations-history',
    definition:
      'Log of all service sales recorded at checkout: amounts, dates, clients, employees, promotions and transaction groups.',
    utility:
      'Audit service revenue, find a past sale, justify a payment, or trace back to a manufacturing order / installment plan via transaction IDs.',
    relatedModules: [
      { label: 'New sale', path: '/pos/transaction', relation: 'Origin of each history line.' },
    ],
    whenToUse: ['Daily / monthly audit', 'Client dispute', 'Trace a manufacturing order'],
    keyActions: ['Filter by period, employee, client', 'Open sale detail', 'Archive if needed'],
    workflow: ['Open history', 'Apply filters', 'Inspect the line and linked documents'],
  },

  '/pos/sales-history': {
    key: '/pos/sales-history',
    title: 'Product sales history',
    path: '/pos/sales-history',
    definition: 'Log of product sales from checkout, with quantities, prices and stock impact.',
    utility: 'Track merchandise revenue and reconcile with stock movements.',
    relatedModules: [
      { label: 'New sale', path: '/pos/transaction', relation: 'Origin of each product sale.' },
      { label: 'Products', path: '/inventory/products', relation: 'Catalog and stock levels.' },
    ],
    whenToUse: ['Product sales control', 'Stock investigation'],
    keyActions: ['Filter', 'Open detail', 'Archive'],
    workflow: ['Open history', 'Filter', 'Inspect the sale'],
  },

  '/pos/reports': {
    key: '/pos/reports',
    title: 'Cash reports',
    path: '/pos/reports',
    definition: 'Cash summaries by period and payment method, with advanced exports.',
    utility: 'Close the till, control collections, export for accounting.',
    relatedModules: [
      { label: 'New sale', path: '/pos/transaction', relation: 'Source of cash-in lines.' },
    ],
    whenToUse: ['End of day', 'Weekly / monthly control'],
    keyActions: ['Choose period', 'Read totals', 'Export advanced PDF/Excel'],
    workflow: ['Open reports', 'Select period and filters', 'Export if needed'],
  },

  '/clients': {
    key: '/clients',
    title: 'Clients',
    path: '/clients',
    definition:
      'Central client directory: identity, contact details, purchase history, loyalty, appointments, installment payments.',
    utility:
      'Single source of truth for clients; required for manufacturing orders, installments, targeted marketing and commercial follow-up.',
    relatedModules: [
      { label: 'New sale', path: '/pos/transaction', relation: 'Each sale can be linked to a profile.' },
      { label: 'Installment payments', path: '/payment-plans', relation: 'Client balances and installments.' },
      { label: 'Manufacturing orders', path: '/manufacturing/orders', relation: 'Orders always linked to an identified client.' },
      { label: 'Calendar', path: '/appointments', relation: 'Past and upcoming appointments.' },
    ],
    whenToUse: ['Create a profile', 'View history', 'Update contact details / consents'],
    keyActions: ['CRUD profiles', 'View sales, appointments, loyalty tabs', 'Multi-criteria search'],
    workflow: ['Search or create', 'Complete the profile', 'Use business tabs'],
  },

  '/inventory/products': {
    key: '/inventory/products',
    title: 'Products',
    path: '/inventory/products',
    definition:
      'Catalog of stockable items: reference, price, current stock, thresholds, barcode, multi-site locations.',
    utility:
      'Reference for checkout sales, supplier deliveries, manufacturing orders, physical inventories; basis of all stock traceability.',
    relatedModules: [
      { label: 'Movements', path: '/inventory/movements', relation: 'In/out history per product.' },
      { label: 'New sale', path: '/pos/transaction', relation: 'Sale and barcode scan.' },
      { label: 'Manufacturing orders', path: '/manufacturing/orders', relation: 'Material consumption on close.' },
      { label: 'Deliveries', path: '/suppliers/deliveries', relation: 'Supplier stock receipts.' },
    ],
    whenToUse: ['Create an item', 'Update price/stock', 'Print barcode labels'],
    keyActions: ['CRUD products', 'Manage barcodes (internal or EAN)', 'Print / PDF labels with or without price'],
    workflow: ['Create the full record', 'Set alert threshold', 'Use at checkout or delivery'],
    importantNotes: ['Barcode labels are available as direct print or PDF.'],
  },

  '/inventory/movements': {
    key: '/inventory/movements',
    title: 'Stock movements',
    path: '/inventory/movements',
    definition:
      'Chronological log of all stock in/out with reason, author, and business links (sale, service, manufacturing order, delivery…).',
    utility:
      'Audit and traceability: understand why stock moved and reconcile with the source sale or manufacturing order.',
    relatedModules: [
      { label: 'Products', path: '/inventory/products', relation: 'Current stock updated by each movement.' },
      { label: 'Manufacturing orders', path: '/manufacturing/orders', relation: '“Close MO” outs with serviceId.' },
      { label: 'Deliveries', path: '/suppliers/deliveries', relation: 'Supplier receipt entries.' },
      { label: 'Product sales history', path: '/pos/sales-history', relation: 'Checkout sale outs.' },
    ],
    whenToUse: ['Inventory variance', 'MO close control', 'Monthly audit'],
    keyActions: ['Filter by product, type, period', 'Read reason and linked references'],
    workflow: ['Filter', 'Identify the movement', 'Trace back to source document (sale, MO, delivery)'],
  },

  '/manufacturing/orders': {
    key: '/manufacturing/orders',
    title: 'Manufacturing orders',
    path: '/manufacturing/orders',
    definition:
      'A manufacturing order (MO) is a production work ticket. It is created automatically when a checkout service is configured to require manufacturing.',
    utility:
      'Track in-progress work and record actual material consumption when closing an order, updating stock.',
    relatedModules: [
      { label: 'New sale', path: '/pos/transaction', relation: 'Creates the MO on payment.' },
      { label: 'Services', path: '/services', relation: 'Manufacturing mode and default material lines.' },
      { label: 'Stock movements', path: '/inventory/movements', relation: 'Outbound movements on MO close.' },
    ],
    whenToUse: ['After a manufacturing sale', 'When closing production and posting real consumption'],
    keyActions: ['Open the order', 'Enter actual quantities', 'Close and update stock'],
    workflow: ['Sale creates the MO', 'Produce', 'Close with actual consumption'],
  },

  '/marketing': {
    key: '/marketing',
    title: 'Marketing',
    path: '/marketing',
    definition:
      'Marketing campaigns: client segments, messages (SMS / email depending on integration), and promotions linked to checkout.',
    utility: 'Run loyalty and retention campaigns with trackable offers at the till.',
    relatedModules: [
      { label: 'Clients', path: '/clients', relation: 'Targeting and loyalty wallet.' },
      { label: 'New sale', path: '/pos/transaction', relation: 'Apply campaign promotions.' },
    ],
    whenToUse: ['Launch a campaign', 'Follow message delivery'],
    keyActions: ['Create a campaign', 'Generate messages', 'Activate / pause'],
    workflow: ['Define audience and offer', 'Generate promotions', 'Send and track'],
  },

  '/messages-clients': {
    key: '/messages-clients',
    title: 'Client messages',
    path: '/messages-clients',
    definition:
      'Queue of automatic or semi-automatic follow-ups after checkout (personalized outreach, often WhatsApp).',
    utility: 'Stay in touch after a visit without leaving the app.',
    relatedModules: [
      { label: 'New sale', path: '/pos/transaction', relation: 'Creates follow-up messages.' },
      { label: 'Clients', path: '/clients', relation: 'Phone and WhatsApp opt-in.' },
    ],
    whenToUse: ['Post-visit follow-up', 'Configure delay templates'],
    keyActions: ['Send via WhatsApp', 'Skip', 'Edit templates'],
    workflow: ['Wait for eligibility delay', 'Open WhatsApp', 'Mark as sent'],
  },

  '/appointments': {
    key: '/appointments',
    title: 'Calendar',
    path: '/appointments',
    definition: 'Appointment calendar: slots, services, clients, resources (employees, rooms…).',
    utility: 'Plan the day and avoid double-booking.',
    relatedModules: [
      { label: 'Clients', path: '/clients', relation: 'Booked client.' },
      { label: 'Services', path: '/services', relation: 'Duration and type.' },
      { label: 'Appointment requests', path: '/appointment-requests', relation: 'Inbound requests to confirm.' },
    ],
    whenToUse: ['Book a visit', 'Reschedule', 'Day overview'],
    keyActions: ['Create', 'Edit', 'Cancel'],
    workflow: ['Pick slot and service', 'Link client', 'Confirm'],
  },

  '/appointment-requests': {
    key: '/appointment-requests',
    title: 'Appointment requests',
    path: '/appointment-requests',
    definition:
      'Inbox of appointment requests from public pages or client forms, waiting for staff validation.',
    utility: 'Convert online demand into confirmed calendar slots.',
    relatedModules: [
      { label: 'Calendar', path: '/appointments', relation: 'Confirmed bookings land here.' },
    ],
    whenToUse: ['Review new public requests', 'Accept or decline'],
    keyActions: ['Validate', 'Refuse', 'Contact the client'],
    workflow: ['Open request', 'Check availability', 'Confirm into the calendar'],
  },

  '/payment-plans': {
    key: '/payment-plans',
    title: 'Installment payments',
    path: '/payment-plans',
    definition:
      'Client balances: a sale can be paid later (0 deposit allowed), with installment tracking and overdue follow-up.',
    utility: 'Sell large tickets without requiring full payment at once.',
    relatedModules: [
      { label: 'New sale', path: '/pos/transaction', relation: 'Create an installment plan from the cart.' },
      { label: 'Clients', path: '/clients', relation: 'Receivables per client.' },
    ],
    whenToUse: ['Partial payment at checkout', 'Record a later installment'],
    keyActions: ['View plan', 'Add payment', 'Close or cancel'],
    workflow: ['Create from POS', 'Collect installments', 'Close when settled'],
  },

  '/referents': {
    key: '/referents',
    title: 'My referents',
    path: '/referents',
    definition:
      'Manage referrers / partners (e.g. doctors) who send clients, with quota or commission.',
    utility: 'Attribute sales to referents and track their contribution.',
    relatedModules: [
      { label: 'New sale', path: '/pos/transaction', relation: 'Link a referent on the cart when enabled.' },
      { label: 'Products', path: '/inventory/products', relation: 'Per-product referent quota.' },
    ],
    whenToUse: ['Register a referrer', 'Review monthly quotas'],
    keyActions: ['Add referent', 'View detail', 'Deactivate'],
    workflow: ['Create the profile', 'Use on sales', 'Review performance'],
  },

  '/dashboard': {
    key: '/dashboard',
    title: 'Dashboard',
    path: '/dashboard',
    definition:
      'Activity overview: KPIs, charts and alerts based on enabled modules and user profile.',
    utility: 'Get a quick health check of the business without opening every module.',
    relatedModules: [
      { label: 'Cash reports', path: '/pos/reports', relation: 'Detailed payment breakdown.' },
      { label: 'Accounting dashboard', path: '/accounting/dashboard', relation: 'Accounting synthesis.' },
    ],
    whenToUse: ['Start of day', 'Manager review'],
    keyActions: ['Read KPIs', 'Drill into alerts'],
    workflow: ['Open dashboard', 'Interpret indicators', 'Open the related module if needed'],
  },

  '/inventory/categories': {
    key: '/inventory/categories',
    title: 'Product categories',
    path: '/inventory/categories',
    definition: 'Classify the product catalog into families (cosmetics, consumables, etc.).',
    utility: 'Filter at checkout and in reports; keep the catalog readable.',
    relatedModules: [
      { label: 'Products', path: '/inventory/products', relation: 'Each product belongs to a category.' },
    ],
    whenToUse: ['Organize the catalog', 'Before creating many products'],
    keyActions: ['Create', 'Edit', 'Delete unused categories'],
    workflow: ['Define families', 'Assign products', 'Use filters in POS / inventory'],
  },

  '/inventory/external-dispatches': {
    key: '/inventory/external-dispatches',
    title: 'External dispatches',
    path: '/inventory/external-dispatches',
    definition:
      'Stock outbound documents to an external destination (other site, third-party warehouse, partner).',
    utility: 'Ship stock outside the internal multi-site transfer flow with a clear trail.',
    relatedModules: [
      { label: 'External destinations', path: '/inventory/external-destinations', relation: 'Partner destinations.' },
      { label: 'Stock movements', path: '/inventory/movements', relation: 'Outbound movements generated.' },
    ],
    whenToUse: ['Send stock to a partner shop', 'Audit external outs'],
    keyActions: ['Create a dispatch', 'Confirm / cancel', 'Print PDF'],
    workflow: ['Pick destination and products', 'Validate', 'Stock decreases'],
  },

  '/inventory/external-destinations': {
    key: '/inventory/external-destinations',
    title: 'External destinations',
    path: '/inventory/external-destinations',
    definition: 'Configure places that receive stock via external dispatches.',
    utility: 'Reuse partner destinations without retyping them on each dispatch.',
    relatedModules: [
      { label: 'External dispatches', path: '/inventory/external-dispatches', relation: 'Uses this directory.' },
    ],
    whenToUse: ['Onboard a partner shop', 'Update contact details'],
    keyActions: ['Create', 'Edit', 'Activate / deactivate'],
    workflow: ['Create destination', 'Use it on a dispatch'],
  },

  '/inventory/audit': {
    key: '/inventory/audit',
    title: 'Physical inventories',
    path: '/inventory/audit',
    definition:
      'Counting sessions: compare theoretical stock (system) with real counted stock.',
    utility: 'Correct variances and keep stock trustworthy.',
    relatedModules: [
      { label: 'Products', path: '/inventory/products', relation: 'Stock adjusted after counting.' },
      { label: 'Stock movements', path: '/inventory/movements', relation: 'Adjustment movements.' },
    ],
    whenToUse: ['Periodic inventory', 'After a discrepancy'],
    keyActions: ['Start a session', 'Count (scan)', 'Apply adjustments'],
    workflow: ['Create session', 'Count', 'Validate adjustments'],
  },

  '/inventory/dashboard': {
    key: '/inventory/dashboard',
    title: 'Stock reports',
    path: '/inventory/dashboard',
    definition: 'Stock indicators: stockouts, low thresholds, turnover, inventory management view.',
    utility: 'Anticipate replenishment before shelves run empty.',
    relatedModules: [
      { label: 'Products', path: '/inventory/products', relation: 'Source of thresholds and quantities.' },
      { label: 'Deliveries', path: '/suppliers/deliveries', relation: 'Restocking after alerts.' },
    ],
    whenToUse: ['Weekly stock review', 'Before ordering'],
    keyActions: ['Read alerts', 'Open critical products'],
    workflow: ['Open reports', 'Prioritize low stock', 'Plan deliveries'],
  },

  '/suppliers': {
    key: '/suppliers',
    title: 'Suppliers',
    path: '/suppliers',
    definition: 'Directory of suppliers and purchasing partners.',
    utility: 'Keep VAT and payment terms ready for every delivery.',
    relatedModules: [
      { label: 'Deliveries', path: '/suppliers/deliveries', relation: 'Receipts linked to a supplier.' },
    ],
    whenToUse: ['Onboard a supplier', 'Update billing mode'],
    keyActions: ['Create', 'Edit', 'View history'],
    workflow: ['Create supplier', 'Configure VAT', 'Use on deliveries'],
  },

  '/suppliers/deliveries': {
    key: '/suppliers/deliveries',
    title: 'Deliveries & transfers',
    path: '/suppliers/deliveries',
    definition:
      'Supplier receipts and inter-site stock transfers (shop, field site, warehouse).',
    utility: 'Increase stock on receipt and move stock between locations.',
    relatedModules: [
      { label: 'Suppliers', path: '/suppliers', relation: 'Source of the delivery.' },
      { label: 'Products', path: '/inventory/products', relation: 'Stock and average cost updated.' },
      { label: 'Stock movements', path: '/inventory/movements', relation: 'Inbound / transfer movements.' },
    ],
    whenToUse: ['Goods arrival', 'Send stock to another site'],
    keyActions: ['Record a delivery', 'Transfer between sites', 'Cancel if needed'],
    workflow: ['Select supplier / sites', 'Add lines', 'Validate'],
  },

  '/expenses': {
    key: '/expenses',
    title: 'Expenses',
    path: '/expenses',
    definition:
      'Record operating charges (rent, electricity, day-to-day purchases…) by category and date.',
    utility: 'Feed accounting and deductible VAT tracking.',
    relatedModules: [
      { label: 'Expense notes', path: '/expense-notes', relation: 'Approved notes become expenses.' },
      { label: 'Accounting journals', path: '/accounting/journals', relation: 'Purchase / expense entries.' },
    ],
    whenToUse: ['Book a charge', 'Export an expense report'],
    keyActions: ['Create', 'Attach receipt', 'Export'],
    workflow: ['Enter amount and category', 'Save', 'Appear in accounting'],
  },

  '/expense-notes': {
    key: '/expense-notes',
    title: 'Expense notes',
    path: '/expense-notes',
    definition:
      'Collaborative workflow: an employee submits a note (amount, date, attachment), an approver validates or rejects, then an expense is created and reimbursement can hit payroll.',
    utility: 'Control out-of-pocket spends before they hit the books.',
    relatedModules: [
      { label: 'Expenses', path: '/expenses', relation: 'Created on approval.' },
      { label: 'Payrolls', path: '/payrolls', relation: 'Reimbursement on salary.' },
    ],
    whenToUse: ['Employee claim', 'Manager approval'],
    keyActions: ['Submit', 'Approve / reject', 'Archive'],
    workflow: ['Draft note', 'Approve', 'Expense + payroll impact'],
  },

  '/accounting/dashboard': {
    key: '/accounting/dashboard',
    title: 'Accounting — Dashboard',
    path: '/accounting/dashboard',
    definition: 'Accounting synthesis: balances, indicators, entry points to journal detail.',
    utility: 'See VAT and margins before exporting.',
    relatedModules: [
      { label: 'Journals', path: '/accounting/journals', relation: 'Detailed entries.' },
      { label: 'VAT returns', path: '/accounting/tax-returns', relation: 'Tax periods.' },
    ],
    whenToUse: ['Month-end close', 'Export summary'],
    keyActions: ['Pick period', 'Export PDF / Excel / CSV'],
    workflow: ['Select period', 'Read synthesis', 'Export if needed'],
  },

  '/accounting/journals': {
    key: '/accounting/journals',
    title: 'Accounting journals',
    path: '/accounting/journals',
    definition: 'List of generated accounting entries (sales, purchases, expenses, payroll…).',
    utility: 'Audit every line feeding the dashboard.',
    relatedModules: [
      { label: 'Accounting dashboard', path: '/accounting/dashboard', relation: 'Aggregated view.' },
    ],
    whenToUse: ['Detail investigation', 'Export a journal'],
    keyActions: ['Filter', 'Export sales / purchases journal'],
    workflow: ['Open journal', 'Filter', 'Export'],
  },

  '/accounting/tax-returns': {
    key: '/accounting/tax-returns',
    title: 'VAT returns',
    path: '/accounting/tax-returns',
    definition: 'Prepare and track VAT returns by tax period.',
    utility: 'Produce a preparatory statement before filing with an accountant.',
    relatedModules: [
      { label: 'Accounting dashboard', path: '/accounting/dashboard', relation: 'Collected / deductible VAT.' },
    ],
    whenToUse: ['Before a VAT filing'],
    keyActions: ['Open period', 'Export declaration'],
    workflow: ['Select period', 'Review amounts', 'Export for review'],
    importantNotes: ['Exports are preparatory — validate with a professional before filing.'],
  },

  '/services': {
    key: '/services',
    title: 'Services',
    path: '/services',
    definition:
      'Catalog of services sold at checkout: name, price, duration (appointments), product consumption or manufacturing model.',
    utility: 'Define what can be sold and how stock / manufacturing reacts.',
    relatedModules: [
      { label: 'New sale', path: '/pos/transaction', relation: 'Services added to the cart.' },
      { label: 'Service categories', path: '/services/categories', relation: 'Grouping.' },
      { label: 'Manufacturing orders', path: '/manufacturing/orders', relation: 'Manufacturing mode.' },
    ],
    whenToUse: ['Create a sellable service', 'Change price or materials'],
    keyActions: ['CRUD', 'Configure consumption / manufacturing'],
    workflow: ['Create service', 'Set price and options', 'Sell at POS'],
  },

  '/services/categories': {
    key: '/services/categories',
    title: 'Service categories',
    path: '/services/categories',
    definition: 'Thematic grouping of services (treatments, prosthesis, consultation…).',
    utility: 'Organize the catalog and POS filters.',
    relatedModules: [
      { label: 'Services', path: '/services', relation: 'Each service has a category.' },
    ],
    whenToUse: ['Structure the menu of services'],
    keyActions: ['Create', 'Edit', 'Delete'],
    workflow: ['Create categories', 'Assign services'],
  },

  '/employees': {
    key: '/employees',
    title: 'Employees',
    path: '/employees',
    definition: 'Staff records: identity, role, app user link, role profile.',
    utility: 'Control who can log in and which permissions they have.',
    relatedModules: [
      { label: 'Role profiles', path: '/settings/role-profiles', relation: 'Permission matrix.' },
      { label: 'Payrolls', path: '/payrolls', relation: 'Salary preparation.' },
    ],
    whenToUse: ['Onboard staff', 'Change access rights'],
    keyActions: ['Create', 'Link user', 'Activate / deactivate'],
    workflow: ['Create employee', 'Assign profile', 'Invite / link login'],
  },

  '/settings/role-profiles': {
    key: '/settings/role-profiles',
    title: 'Role profiles',
    path: '/settings/role-profiles',
    definition:
      'Permission matrices: which modules and actions each profile can see (POS, stock, payroll, expense-note approval…).',
    utility: 'Standardize access without editing each employee one by one.',
    relatedModules: [
      { label: 'Employees', path: '/employees', relation: 'Profile assigned to staff.' },
    ],
    whenToUse: ['Define cashier vs manager rights', 'Duplicate a profile'],
    keyActions: ['Create', 'Edit permissions', 'Duplicate', 'Delete'],
    workflow: ['Create profile', 'Tick permissions', 'Assign to employees'],
  },

  '/payrolls': {
    key: '/payrolls',
    title: 'Payrolls',
    path: '/payrolls',
    definition: 'Prepare, validate and track monthly payrolls per employee.',
    utility: 'Close the month salaries with adjustments and expense reimbursements.',
    relatedModules: [
      { label: 'Employees', path: '/employees', relation: 'Payroll subjects.' },
      { label: 'Expense notes', path: '/expense-notes', relation: 'Approved reimbursements.' },
    ],
    whenToUse: ['Month-end payroll', 'Mark as paid'],
    keyActions: ['Generate', 'Validate', 'Mark paid', 'Adjustments'],
    workflow: ['Generate month', 'Review', 'Validate then pay'],
  },

  '/performance-indicators': {
    key: '/performance-indicators',
    title: 'Performance indicators',
    path: '/performance-indicators',
    definition: 'Numeric targets (revenue, volumes…) per employee or team over a period.',
    utility: 'Align objectives and review achievement.',
    relatedModules: [
      { label: 'Employees', path: '/employees', relation: 'Target owners.' },
      { label: 'Cash reports', path: '/pos/reports', relation: 'Actuals from sales.' },
    ],
    whenToUse: ['Set monthly targets', 'Review performance'],
    keyActions: ['Create sheet', 'Edit targets', 'Delete'],
    workflow: ['Define period and targets', 'Compare with actuals'],
  },

  '/activity/reports': {
    key: '/activity/reports',
    title: 'Activity reports',
    path: '/activity/reports',
    definition:
      'Commercial or operational activity write-ups (actions, volumes, comments), with draft → validation workflow.',
    utility: 'Structure field activity reporting; Excel/PDF attachments for evidence.',
    relatedModules: [
      { label: 'Opportunities', path: '/activity/opportunities', relation: 'Actions can feed the pipeline.' },
      { label: 'Pipeline setup', path: '/settings/pipeline', relation: 'Linked sales stages.' },
    ],
    whenToUse: ['Weekly commercial reporting', 'Field action follow-up'],
    keyActions: ['Write report', 'Autosave draft (mobile)', 'Validate', 'Attach files'],
    workflow: ['Enter activities', 'Check draft saved', 'Submit for validation'],
    importantNotes: ['Draft is autosaved locally to avoid losing input (calls, etc.).'],
  },

  '/activity/opportunities': {
    key: '/activity/opportunities',
    title: 'Opportunities',
    path: '/activity/opportunities',
    definition: 'Kanban-style sales pipeline: deals by stage, amounts, probabilities.',
    utility: 'Visualize the commercial portfolio and prioritize follow-ups.',
    relatedModules: [
      { label: 'Pipeline setup', path: '/settings/pipeline', relation: 'Kanban columns.' },
      { label: 'Activity reports', path: '/activity/reports', relation: 'Field input.' },
      { label: 'Clients', path: '/clients', relation: 'Prospect → client conversion.' },
    ],
    whenToUse: ['Track deals', 'Move a card to the next stage'],
    keyActions: ['Create', 'Drag between stages', 'Delete'],
    workflow: ['Create opportunity', 'Advance stages', 'Close won / lost'],
  },

  '/settings/pipeline': {
    key: '/settings/pipeline',
    title: 'Pipeline setup',
    path: '/settings/pipeline',
    definition: 'Configure commercial funnel stages (name, order) used in Opportunities.',
    utility: 'Adapt the kanban to your sales process.',
    relatedModules: [
      { label: 'Opportunities', path: '/activity/opportunities', relation: 'Uses these stages.' },
    ],
    whenToUse: ['Initial setup', 'Add a stage'],
    keyActions: ['Create pipeline', 'Reorder stages'],
    workflow: ['Define stages', 'Save', 'Use in Opportunities'],
  },

  '/mon-paiement': {
    key: '/mon-paiement',
    title: 'Licence payment',
    path: '/mon-paiement',
    definition:
      'Client area to view the Nauvatek contract and pay licence installments already created by sales (CSM / Super Admin). Online payment goes through the configured provider.',
    utility: 'Let the business settle its Nauvatek subscription without admin intervention.',
    relatedModules: [],
    whenToUse: ['Pay an open licence invoice'],
    keyActions: ['View amounts due', 'Pay online'],
    workflow: ['Open the page', 'Select an installment', 'Pay'],
  },
};
